import React, { useEffect, useState, useMemo } from "react";
import { userApiService } from "../../../service/admin/userApiService";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaSync, FaCheck, FaTimes, FaCalendarAlt } from "react-icons/fa";
import ExcelTable from "../../../component/library/excelTable";
import { getAuthLocalStorage } from "../../../storage/localStorage";
import "./style.css";

const UserStatistics = () => {
  const [userStats, setUserStats] = useState([]);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState({ currentPage: 1, totalPages: 1, totalItems: 0 });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchData, setSearchData] = useState({
    search: "",
    startDate: "",
    endDate: "",
    marketplaceFilter: "",
  });

  const message = useMessage();
  const navigate = useNavigate();

  // Get current user ID for per-user table settings
  const authData = getAuthLocalStorage();
  const currentUserId = authData?.loginHeader || null;

  const userTypeOptions = [
    { value: "", label: "--Select--" },
    { value: "potential", label: "Potential" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [searchData.marketplaceFilter, searchData.search, searchData.startDate, searchData.endDate]);

  useEffect(() => {
    fetchAllStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, searchData.marketplaceFilter, searchData.search, searchData.startDate, searchData.endDate]);

  const fetchAllStatistics = () => {
    setLoading(true);
    // Parse marketplaceFilter: "shopify" -> marketplace=shopify&connectionStatus=yes, "amazon" -> marketplace=amazon&connectionStatus=yes
    let marketplace = "";
    let connectionStatus = "";
    if (searchData.marketplaceFilter === "shopify") {
      marketplace = "shopify";
      connectionStatus = "yes";
    } else if (searchData.marketplaceFilter === "amazon") {
      marketplace = "amazon";
      connectionStatus = "yes";
    }
    const query = `?search=${searchData?.search || ""}&role=seller&startDate=${searchData?.startDate || ""}&endDate=${searchData?.endDate || ""}&marketplace=${marketplace}&connectionStatus=${connectionStatus}&page=${page}&limit=${limit}&includeOrderCount=true`;
    userApiService
      .getUserList(query)
      .then((res) => {
        if (res.status.code === 200) {
          const usersWithStatus = res.data.users.map((user) => ({
            ...user,
            status: user.status || "",
          }));
          setUserStats(usersWithStatus);
          setPages({
            currentPage: res.data.currentPage,
            totalPages: res.data.totalPages,
            totalItems: res.data.totalUsers,
          });
        } else if (res.status.code === 402) {
          navigate("/login");
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        message.showToast(err?.message || "Server error", ToastTypes.ERROR);
      })
      .finally(() => {
        setLoading(false);
        setInitialLoading(false);
      });
  };

  const handleOnChange = (e) => {
    setSearchData({ ...searchData, [e.target.name]: e.target.value });
  };

  const handleClearFilters = () => {
    setSearchData({
      search: "",
      startDate: "",
      endDate: "",
      marketplaceFilter: "",
    });
    setPage(1);
  };

  const handleSelectionChange = (selected) => {
    setSelectedUsers(selected);
  };

  const handleDropdown = (userId, value) => {
    // Update local state
    setUserStats((prev) =>
      prev.map((user) =>
        user._id === userId ? { ...user, userType: value } : user
      )
    );
    // Update in backend
    userApiService
      .updateUser(userId, { userType: value })
      .then((res) => {
        if (res.status.code === 200) {
          message.showToast("User type updated", ToastTypes.SUCCESS);
        }
      })
      .catch((err) => {
        message.showToast(err?.message || "Error updating", ToastTypes.ERROR);
      });
  };

  const handleRowNavigate = (user) => {
    if (!user?._id) return;
    navigate(`/admin/user-management/edit-user/${user._id}`);
  };

  const currentPage = Number(pages?.currentPage) || 1;
  const pageSize = Number(limit) || 0;
  const effectivePageSize = pageSize || (userStats?.length ?? 0);

  const tableData = useMemo(
    () =>
      (userStats || []).map((item, index) => ({
        ...item,
        serialNumber: (currentPage - 1) * effectivePageSize + index + 1,
      })),
    [userStats, currentPage, effectivePageSize]
  );

  const formatCurrency = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "₹0";
    return `₹${num.toLocaleString("en-IN")}`;
  };

  const columns = [
    {
      key: "serialNumber",
      label: "S.No.",
      width: 60,
      render: (value) => <span className="us-serial">{value ?? "-"}</span>,
    },
    {
      key: "firstName",
      label: "User Name",
      width: 160,
      render: (_, row) => (
        <span
          className="us-name-link"
          onClick={(e) => {
            e.stopPropagation();
            handleRowNavigate(row);
          }}
          style={{
            color: "#0969da",
            cursor: "pointer",
            textDecoration: "underline",
            fontWeight: "500"
          }}
          onMouseEnter={(e) => e.target.style.color = "#0860ca"}
          onMouseLeave={(e) => e.target.style.color = "#0969da"}
        >
          {row.firstName} {row.lastName}
        </span>
      ),
    },
    {
      key: "email",
      label: "Email ID",
      width: 200,
      render: (value) => <span className="us-email">{value || "-"}</span>,
    },
    {
      key: "createdAt",
      label: "Registered Date",
      width: 120,
      render: (value) =>
        value ? moment(value).format("DD/MM/YYYY") : "-",
    },
    {
      key: "joiningDays",
      label: "Total Days",
      width: 90,
      render: (value) => <span className="us-center">{value || 0}</span>,
    },
    {
      key: "totalOrders",
      label: "Total Orders",
      width: 120,
      render: (value) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <span 
            style={{ 
              fontWeight: "700", 
              fontSize: "13px",
              color: value > 0 ? "#0969da" : "#6b7280",
              background: value > 0 ? "#dbeafe" : "#f3f4f6",
              padding: "4px 10px",
              borderRadius: "5px",
              display: "inline-block",
              minWidth: "45px",
              textAlign: "center",
              border: value > 0 ? "1px solid #60a5fa" : "1px solid #d1d5db"
            }}
          >
            {value || 0}
          </span>
        </div>
      ),
    },
    {
      key: "processOrders",
      label: "Processed",
      width: 90,
      render: (value) => <span className="us-center">{value || 0}</span>,
    },
    {
      key: "walletBalance",
      label: "Wallet",
      width: 100,
      render: (value) => (
        <span className="us-currency">{formatCurrency(value)}</span>
      ),
    },
    {
      key: "depositBalance",
      label: "Deposit",
      width: 90,
      render: (value) => (
        <span className="us-currency">{formatCurrency(value)}</span>
      ),
    },
    {
      key: "refundBalance",
      label: "Refund",
      width: 90,
      render: (value) => (
        <span className="us-currency">{formatCurrency(value)}</span>
      ),
    },
    {
      key: "shopifyApiConnect",
      label: "Shopify API",
      width: 100,
      render: (value) => (
        <span className={`us-api-badge ${value ? "us-api-yes" : "us-api-no"}`}>
          {value ? <><FaCheck /> Yes</> : <><FaTimes /> No</>}
        </span>
      ),
    },
    {
      key: "amazonApiConnect",
      label: "Amazon API",
      width: 100,
      render: (value) => (
        <span className={`us-api-badge ${value ? "us-api-yes" : "us-api-no"}`}>
          {value ? <><FaCheck /> Yes</> : <><FaTimes /> No</>}
        </span>
      ),
    },
    {
      key: "sellerFailedOrderCount",
      label: "Failed Orders",
      width: 100,
      render: (value) => (
        <span className={`us-center ${value > 0 ? "us-failed" : ""}`}>
          {value || 0}
        </span>
      ),
    },
    {
      key: "productSubscribe",
      label: "Subscribed",
      width: 90,
      render: (value) => (
        <span className="us-center">{Array.isArray(value) ? value.length : 0}</span>
      ),
    },
    {
      key: "totalLoginCount",
      label: "Logins",
      width: 80,
      render: (value) => <span className="us-center">{value || 0}</span>,
    },
    {
      key: "weeklyLoginCount",
      label: "Weekly Logins",
      width: 100,
      render: (value) => <span className="us-center">{value || 0}</span>,
    },
    {
      key: "userType",
      label: "User Type",
      width: 130,
      render: (value, row) => (
        <select
          className="us-type-select"
          value={value || ""}
          onChange={(e) => {
            e.stopPropagation();
            handleDropdown(row._id, e.target.value);
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {userTypeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ),
    },
  ];

  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, pages.totalItems);

  const toolbarLeftActions = (
    <div className="us-toolbar-left">
      <div className="us-search-box">
        <FaSearch className="us-search-icon" />
        <input
          type="text"
          className="us-search-input"
          placeholder="Search by name, email..."
          value={searchData.search}
          name="search"
          onChange={handleOnChange}
          onKeyUp={(e) => e.key === "Enter" && fetchAllStatistics()}
        />
      </div>
    </div>
  );

  const toolbarRightActions = (
    <div className="us-toolbar-right">
      <button className="us-refresh-btn" onClick={fetchAllStatistics} title="Refresh">
        <FaSync className={loading ? "us-spin" : ""} />
      </button>
      <div className="us-rows-picker">
        <span>Rows:</span>
        <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}>
          {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
    </div>
  );

  return (
    <div className="us-page">
      {/* Filter Section - Above Table */}
      <div className="us-filter-section">
        <div className="us-filter-container">
          <div className="us-date-box">
            <FaCalendarAlt className="us-date-icon" />
            <input
              type="date"
              className="us-date-input"
              value={searchData.startDate}
              name="startDate"
              onChange={handleOnChange}
            />
          </div>
          <span className="us-date-separator">to</span>
          <div className="us-date-box">
            <FaCalendarAlt className="us-date-icon" />
            <input
              type="date"
              className="us-date-input"
              value={searchData.endDate}
              name="endDate"
              onChange={handleOnChange}
            />
          </div>
          <div className="us-filter-box">
            <select
              className="us-filter-select"
              value={searchData.marketplaceFilter}
              name="marketplaceFilter"
              onChange={handleOnChange}
            >
              <option value="">All Marketplaces</option>
              <option value="shopify">Shopify Connected</option>
              <option value="amazon">Amazon Connected</option>
            </select>
          </div>
          <button className="us-search-btn" onClick={fetchAllStatistics}>
            <FaSearch /> Search
          </button>
          <button className="us-clear-btn" onClick={handleClearFilters}>
            Clear Filters
          </button>
        </div>
      </div>

      <div className="us-table-section">
        <ExcelTable
          data={tableData}
          columns={columns}
          isLoading={initialLoading || loading}
          loadingText="Loading user statistics..."
          showToolbarExport={true}
          showToolbarFilter={false}
          showToolbarColumns={true}
          showToolbarFullscreen={true}
          toolbarLeftActions={toolbarLeftActions}
          toolbarRightActions={toolbarRightActions}
          onSelectionChange={handleSelectionChange}
          selectedData={selectedUsers}
          selectable={true}
          sortable={true}
          resizable={true}
          showToolbar={true}
          title="User-Statistics"
          userId={currentUserId}
        />

        {userStats.length > 0 && (
          <div className="us-pagination">
            <div className="us-pagination-info">
              Showing {startItem} to {endItem} of {(Number(pages.totalItems) || 0).toLocaleString()} users
            </div>
            <div className="us-pagination-controls">
              <button className="us-page-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
              {Array.from({ length: Math.min(5, pages.totalPages) }, (_, i) => {
                let pageNum;
                if (pages.totalPages <= 5) pageNum = i + 1;
                else if (page <= 3) pageNum = i + 1;
                else if (page >= pages.totalPages - 2) pageNum = pages.totalPages - 4 + i;
                else pageNum = page - 2 + i;
                return (
                  <button
                    key={pageNum}
                    className={`us-page-btn ${page === pageNum ? "us-page-btn-active" : ""}`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              {pages.totalPages > 5 && page < pages.totalPages - 2 && (
                <>
                  <span className="us-page-dots">...</span>
                  <button className="us-page-btn" onClick={() => setPage(pages.totalPages)}>{pages.totalPages}</button>
                </>
              )}
              <button className="us-page-btn" disabled={page >= pages.totalPages} onClick={() => setPage(page + 1)}>›</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserStatistics;
